import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import {
  addDataSourceSuccess,
  deleteDataSourceSuccess,
  loadDataSources,
  updateDataSourceSuccess,
} from "../data-source/state/data-source.actions";
import {
  selectChats,
  selectSurveys,
} from "../data-source/state/data-source.selectors";
import { DataSourceSummary } from "../data-source/state/union.model";
import { UpdateJobProgress, UpdateJobStatus } from "../job/state/job.actions";
import { Job, JobProcess, PipeNames, PipeStatus } from "../job/state/job.model";
import { ModelState } from "../models/state/model";
import {
  AddModelSuccess,
  CloneModelVersionSuccess,
  DeleteModelSuccess,
  DeleteModelVersionSuccess,
  MakeModelObsoleteSuccess,
  ReleaseModelSuccess,
  UpdateModelLabelSuccess,
  UpdateModelVersionSuccess,
} from "../models/state/model.actions";
import { selectModelState } from "../models/state/model.selectors";
import { addPersistentNotification } from "../persistent-notification/state/persistent-notification.actions";
import { PersistentNotification } from "../persistent-notification/state/persistent-notification.model";
import { updatePipelineProgress } from "../pipelines/state/pipeline-progress.actions";
import { ToastService } from "../shared/toast.service";
import {
  EventType,
  HomingPigeonMessage,
  IQErrorMessage,
} from "./homing-pigeon.model";

@Injectable({ providedIn: "root" })
export class AsyncMessageHandlerService {
  surveyState: DataSourceSummary[];
  chatState: DataSourceSummary[];
  sentimentState: ModelState;

  constructor(
    private store: Store<any>,
    private logger: NGXLogger,
    private toastService: ToastService,
  ) {
    this.store.select(selectSurveys).subscribe((s) => (this.surveyState = s));
    this.store.select(selectChats).subscribe((c) => (this.chatState = c));
    this.store
      .select(selectModelState)
      .subscribe((s) => (this.sentimentState = s));
  }

  public handleMessage(message: HomingPigeonMessage) {
    switch (message.type) {
      case EventType.DATA_SOURCE_CREATED:
        this.store.dispatch(
          addDataSourceSuccess({ dataSource: message.body.data }),
        );
        this.toastService.openToast(
          `Data Source "${message.body.data.label}" was created by ${message.body.user.email}`,
        );
        break;
      case EventType.DATA_SOURCE_UPDATED:
        this.store.dispatch(
          updateDataSourceSuccess({ dataSource: message.body.data }),
        );
        this.toastService.openToast(
          `Data Source "${message.body.data.label}" was updated by ${message.body.user.email}`,
        );
        break;
      case EventType.DATA_SOURCE_DELETED:
        this.store.dispatch(
          deleteDataSourceSuccess({ id: message.body.data.uuid }),
        );
        this.toastService.openToast(
          `Data Source "${message.body.data.label}" was deleted by ${message.body.user.email}`,
        );
        break;
      case EventType.JOB_STATUS:
        const status = message.body.status;
        const job = message.body.job;
        const pipeline = job.pipeline ? job.pipeline : [];
        this.store.dispatch(new UpdateJobStatus(job, status));
        if (pipeline.includes(PipeNames.PERSISTENCE_SURVEY)) {
          this.handleSurveyStatusChange(status, job);
        } else if (pipeline.includes(PipeNames.PERSISTENCE_CHAT)) {
          this.handleChatStatusChange(status, job);
        } else if (
          pipeline.includes(PipeNames.RAVEN) &&
          job.configuration.raven_process === JobProcess.UPDATE_ML
        ) {
          this.handleUpdateMlStatusChange(status, job);
        } else if (
          pipeline.includes(PipeNames.FLAMINGO) &&
          job.configuration.flamingo_process === "update_sentiment" &&
          status === PipeStatus.FINISHED
        ) {
          this.store.dispatch(loadDataSources());
          this.toastService.openToast(
            `Sentiment Lexicon has finished applying`,
          );
        }
        break;

      case EventType.JOB_PROGRESS:
        this.store.dispatch(
          new UpdateJobProgress(
            message.body.job,
            message.body.pipe,
            message.body.progress,
            message.body.payload,
          ),
        );
        break;

      case EventType.MODEL_CREATED:
        this.store.dispatch(new AddModelSuccess(message.body.data));
        this.toastService.openToast(
          `Model "${message.body.data.label}" was created by ${message.body.user.email}`,
        );
        break;

      case EventType.MODEL_UPDATED:
        this.store.dispatch(
          new UpdateModelLabelSuccess({
            ...message.body.data,
            label: message.body.data.label,
          }),
        );
        this.toastService.openToast(
          `Model "${message.body.data.label}" was changed by ${message.body.user.email}`,
        );
        break;

      case EventType.MODEL_OBSOLETE:
        this.store.dispatch(
          new MakeModelObsoleteSuccess({
            ...message.body.data,
            obsolete: true,
          }),
        );
        this.toastService.openToast(
          `Model "${message.body.data.label}" was made as obsolete by ${message.body.user.email}`,
        );
        break;

      case EventType.MODEL_DELETED:
        this.store.dispatch(new DeleteModelSuccess(message.body.data.uuid));
        this.toastService.openToast(
          `Model "${message.body.data.label}" was deleted by ${message.body.user.email}`,
        );
        break;

      case EventType.MODEL_VERSION_UPDATED:
        const [modelUpdated] = this.sentimentState.summaries.filter(
          (item) => item.uuid === message.body.data.model_id,
        );
        const newVersion = message.body.data;
        this.store.dispatch(new UpdateModelVersionSuccess(newVersion));
        break;

      case EventType.MODEL_VERSION_CREATED:
        this.store.dispatch(new CloneModelVersionSuccess(message.body.data));
        const [modelCreated] = this.sentimentState.summaries.filter(
          (item) => item.uuid === message.body.data.model_id,
        );
        const modelLabel =
          modelCreated?.label ??
          message.body.data?.label ??
          message.body.data?.model_id;
        this.toastService.openToast(
          `A Model version of "${modelLabel}" was created by ${message.body.user.email}`,
        );
        break;

      case EventType.MODEL_VERSION_RELEASED:
        this.store.dispatch(new ReleaseModelSuccess(message.body.data));
        this.toastService.openToast(
          `A version of Model "${message.body.data.label}" was released by ${message.body.user.email}`,
        );
        break;

      case EventType.MODEL_VERSION_DELETED:
        this.store.dispatch(
          new DeleteModelVersionSuccess(message.body.data.uuid),
        );
        this.toastService.openToast(
          `Model version ${message.body.data.label} was deleted by ${message.body.user.email}`,
        );
        break;

      case EventType.CHAT_SESSION_DELETED:
        this.store.dispatch(loadDataSources());
        this.toastService.openToast(
          `Chat session "${message.body.data.session}" was deleted by ${message.body.user.email}`,
        );
        break;

      case EventType.PERSISTENT_NOTIFICATION_CREATED:
        const notification: PersistentNotification = message.body.data;
        this.store.dispatch(addPersistentNotification({ notification }));
        this.toastService.openToast(`New notification: ${notification.type}`);
        break;

      case EventType.PIPELINE_UPDATED:
        this.store.dispatch(updatePipelineProgress({ pipeline: message.body }));
        break;

      default:
        this.logger.warn(
          `Unhandled homing pigeon tenant message type: ${message.type}`,
          message,
        );
        break;
    }
  }

  public handleError(errorMessage: IQErrorMessage) {
    this.logger.error(`Homing Pigeon Error: ${errorMessage.body.type}`);
  }

  protected handleSurveyStatusChange(status: string, job: Job) {
    switch (status) {
      case PipeStatus.FINISHED:
        // Get the survey's name and toast
        const survey = this.surveyState.find(
          (s) => s.uuid === job.configuration.import_def_uuid,
        );
        if (survey) {
          this.toastService.openToast(`${survey.label} import finished!`);
        }

        // Update the list of all surveys
        this.store.dispatch(loadDataSources());
        break;
      case PipeStatus.ERROR: // TODO: Once we have a pattern for error notifications
      case PipeStatus.BATCHING:
      case PipeStatus.PROCESSING:
      default:
        break;
    }
  }

  protected handleChatStatusChange(status: string, job: Job) {
    switch (status) {
      case PipeStatus.FINISHED:
        // Get the chats's name and toast
        const chat = this.chatState.find(
          (c) => c.uuid === job.configuration.chat_uuid,
        );
        if (chat) {
          this.toastService.openToast(`${chat.label} import finished!`);
        }

        // Update the list of all chats
        this.store.dispatch(loadDataSources());
        break;
      case PipeStatus.ERROR: // TODO: Once we have a pattern for error notifications
      case PipeStatus.BATCHING:
      case PipeStatus.PROCESSING:
      default:
        break;
    }
  }

  protected handleUpdateMlStatusChange(status: string, job: Job) {
    switch (status) {
      case PipeStatus.FINISHED:
        // Get the chats' name and toast
        const chat = this.chatState.find(
          (c) => c.uuid === job.configuration.data_source_uuid,
        );
        const survey = this.surveyState.find(
          (s) => s.uuid === job.configuration.data_source_uuid,
        );
        const dataSource = chat ? chat : survey;
        if (dataSource) {
          this.toastService.openToast(`Model applied to ${dataSource.label}!`);
        }

        // Update the list of all chats
        this.store.dispatch(loadDataSources());
        break;
      case PipeStatus.ERROR: // TODO: Once we have a pattern for error notifications
      case PipeStatus.BATCHING:
      case PipeStatus.PROCESSING:
      default:
        break;
    }
  }
}
